import React, { useState, useEffect } from "react";
import MetricCard from "./MetricCard";
import "../styles/metricsWidget.css";
import { PlusOutlined } from '@ant-design/icons';
import { Card, Modal, Input, Form, Select, Checkbox, Col, Row, Button } from 'antd';
import { BASE_URL } from "../../Components/Constant";
import ApiService from "../../Components/apiService.js";
import FiltersDynamic from "../../Components/FiltersDynamicComponent";
const { Option } = Select;
const defaultMetric = () => ({
  id: Date.now() + Math.random(),
  label: '',
  objectName: '',
  fieldName: '',
  aggregationType: 'count',
  showGraph: true,
  filters: [{ field: '', value: '', displayValue: '' }],
  logic: '',
});
const MetricsWidget = ({ minItems = 1, onMetricsChange, mtrics, isEditingWindow }) => {

  const [metrics, setMetrics] = useState(() => {
    if (Array.isArray(mtrics) && mtrics?.length > 0) {
      return mtrics?.map(m => ({
        id: m?.id || Date.now() + Math.random(),
        label: m?.label || '',
        objectName: m?.objectName || '',
        fieldName: m?.fieldName || '',
        aggregationType: m?.aggregationType || 'count',
        fieldType: m?.fieldType || 'integer',
        showGraph: typeof m?.showGraph === 'boolean' ? m.showGraph : true,
        iconType: m?.iconType,
        filters: m?.filters || [{ field: '', value: '', displayValue: '' }],
        logic: m?.logic || '',
      }));
    }
    return Array.from({ length: minItems }, defaultMetric);
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMetricId, setEditingMetricId] = useState(null);
  const [objects, setObjects] = useState([]);
  const [fetchingObjects, setFetchingObjects] = useState(false);
  const [fetchingFields, setFetchingFields] = useState(false);
  const [fieldsDataMap, setFieldsDataMap] = useState({}); // key: metricId, value: fields array
  const [form] = Form.useForm();
  // FiltersDynamic state variables
  const [logic, setLogic] = useState('');
  const [lookupOptions, setLookupOptions] = useState([]); // For storing lookup options
  const [operator, setOperator] = useState('AND');
  const [isLogicEditable, setIsLogicEditable] = useState(false);
  const [filters, setFilters] = useState([{ field: '', value: '', displayValue: '' }]); // For managing filters
  const [valueOperator, setValueOperator] = useState('');
  const [datefields, setDatefields] = useState([]);


  useEffect(() => {
    if (Array.isArray(mtrics)) {
      setMetrics(
        mtrics.map(m => ({
          id: m?.id || Date.now() + Math.random(),
          label: m?.label || '',
          objectName: m?.objectName || '',
          fieldName: m?.fieldName || '',
          aggregationType: m?.aggregationType || 'count',
          fieldType: m?.fieldType || 'integer',
          showGraph: typeof m?.showGraph === 'boolean' ? m.showGraph : true,
          iconType: m?.iconType,
          filters: m?.filters || [{ field: '', value: '', displayValue: '' }],
          logic: m?.logic || '',
        }))
      );
    }
  }, [mtrics]);



  useEffect(() => {
    fetchObjects().then(objects => {
      setObjects(objects);
      setFetchingObjects(false);
    }).catch(error => {
      setFetchingObjects(false);
      console.error("Error fetching objects:", error);
    });
  }, []);

  const fetchFields = async (objectName, metricId) => {
    try {
      setFetchingFields(true);
      const apiService = new ApiService(`${BASE_URL}/mt_fields/object/${objectName}`, {
        "Content-Type": "application/json",
      });
      const res = await apiService.makeCall();
      // Filter fields to keep only integer, decimal, percentage, and currency types
      const allowedTypes = ["integer", "decimal", "percentage", "currency", "rollup"];
      const filteredFields = (res || []).filter(field =>
        allowedTypes.includes(field.type) && field.name !== "recordCount"
      );

      setDatefields(res.filter(field =>
        field.type === "date" || field.type === "datetime"
      ));

      setFieldsDataMap(prev => ({
        ...prev,
        [metricId]: filteredFields,
        [`${metricId}_all`]: res || [], // Store all fields for filters
      }));
      setFetchingFields(false);
    } catch (error) {
      setFetchingFields(false);
      console.error("Error fetching fields:", error);
    }
  };
  // Function to fetch records for lookup fields
  const fetchLookupRecords = async (objectName) => {
    try {
      const apiServiceForRecords = new ApiService(
        `${BASE_URL}/fetch_records/${objectName}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiServiceForRecords.makeCall();

      // Assuming response is an array of records with _id and Name fields
      const records = response?.records?.map(record => ({
        value: record._id, // Set _id as value
        label: record.Name, // Set Name as label
      }));
      setLookupOptions(records); // Set options for the lookup field
    } catch (error) {
      console.error("Error fetching lookup records:", error);
    }
  };
  const fetchLookupRecordsForExistingFilters = (existingFilters) => {
    const allFields = fieldsDataMap[`${editingMetricId}_all`] || [];
    existingFilters.forEach(filter => {
      const selectedField = allFields.find(field => field.name === filter?.field?.replace('_id', ''));
      if (selectedField && selectedField.type === 'lookup') {
        fetchLookupRecords(selectedField.parent_object_name); // Fetch records for lookup fields
      }
    });
  };
  const addMetric = () => {
    const newMetrics = [...metrics, defaultMetric()];
    setMetrics(newMetrics);
    if (onMetricsChange) onMetricsChange(newMetrics);
  };
  const removeMetric = (id) => {
    if (metrics.length > minItems) {
      const newMetrics = metrics.filter((m) => m.id !== id);
      setMetrics(newMetrics);
      if (onMetricsChange) onMetricsChange(newMetrics);
    }
  };
  const openConfigureModal = (id) => {
    setEditingMetricId(id);
    const metric = metrics.find((m) => m.id === id);
    form.setFieldsValue({
      label: metric?.label || "",
      objectName: metric?.objectName || "",
      fieldName: metric?.fieldName || "",
      aggregationType: metric?.aggregationType || "count",
      fieldType: metric?.fieldType || "integer",
      showGraph: typeof metric?.showGraph === 'boolean' ? metric.showGraph : true,
    });

    // Initialize filters for this metric
    setFilters(metric?.filters || [{ field: '', value: '', displayValue: '' }]);
    setLogic(metric?.logic || '');

    // Fetch fields for this metric's object if present
    if (metric?.objectName) {
      fetchFields(metric.objectName, id);
    }
    setIsModalVisible(true);
  };
  const handleModalOk = () => {
    form.validateFields().then(values => {
      setMetrics(prevMetrics => {
        const newMetrics = prevMetrics.map(m =>
          m.id === editingMetricId
            ? {
              ...m,
              label: values.label,
              objectName: values.objectName,
              fieldName: values.fieldName,
              aggregationType: values.aggregationType,
              fieldType: fieldsDataMap[editingMetricId]?.find(f => f.name === values.fieldName)?.type || 'integer',
              showGraph: typeof values.showGraph === 'boolean' ? values.showGraph : true,
              iconType: values.iconType || 'user',
              filters: filters,
              logic: logic,
              datefield: values.dataField,
            }
            : m
        );
        if (onMetricsChange) onMetricsChange(newMetrics);
        console.log("Updated metrics:", newMetrics);
        return newMetrics;
      });
      setIsModalVisible(false);
      setEditingMetricId(null);
      // Reset filters state
      setFilters([{ field: '', value: '', displayValue: '' }]);
      setLogic('');
    });
  };
  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingMetricId(null);
    // Reset filters state
    setFilters([{ field: '', value: '', displayValue: '' }]);
    setLogic('');
  };
  const renderAddCard = () => (
    <div key="add-card">
      <Card
        style={{
          width: "240px",
          minHeight: "160px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed #1890ff",
          borderRadius: '12px',
          background: "#fafcff",
          cursor: "pointer",
        }}
        bodyStyle={{ padding: 0, height: "100%" }}
        onClick={addMetric}
      >
        <PlusOutlined style={{ fontSize: 32, color: "#1890ff" }} />
      </Card>
    </div>
  );
  const fetchObjects = async () => {
    setFetchingObjects(true);
    const objectsService = new ApiService(`${BASE_URL}/mt_objects`, {
      'Content-Type': 'application/json',
    }, 'GET');
    const objectsResponse = await objectsService.makeCall();
    if (objectsResponse) {
      return objectsResponse?.map(obj => ({
        _id: obj?._id,
        label: obj?.label,
        name: obj?.name,
      }));
    }
    return [];
  }
  const handleObjectChange = (value) => {
    form.setFieldsValue({ fieldName: undefined }); // reset fieldName
    fetchFields(value, editingMetricId);
    // Reset filters when object changes
    setFilters([{ field: '', value: '', displayValue: '' }]);
    setLogic('');
  };
  return (
    <div className="metrics-container rdg-light non-draggable" style={{ maxHeight: "80vh", overflowY: "hidden" }}>
      {style}
      <div style={{ display: "flex", flexDirection: "row", gap: "12px", paddingBottom: "6px" }}>
        {metrics.map((metric) => {
          const fieldObj = (fieldsDataMap[metric.id] || []).find(f => f.name === metric.fieldName);
          const effectiveFieldType = fieldObj?.type || metric.fieldType;
          return (
            <div key={metric.id}>
              <MetricCard
                id={metric.id}
                label={metric.label}
                objectToAggregate={metric.objectName}
                fieldName={metric.fieldName}
                aggregationType={metric.aggregationType}
                onRemove={removeMetric}
                disableRemove={metrics.length <= minItems}
                onConfigure={openConfigureModal}
                isEditingWindow={isEditingWindow}
                fields={fieldsDataMap[metric.id] || []}
                fieldType={effectiveFieldType}
                rollupReturnType={fieldObj?.rollup_return_type}
                fetchFields={fetchFields}
                metricObjectName={metric?.objectName}
                metricId={metric?.id}
                showGraph={typeof metric.showGraph === 'boolean' ? metric.showGraph : true}
                iconType={metric.iconType || 'user'}
                filters={metric.filters}
                logic={metric.logic}
                datefield={metric.datefield}
              />
            </div>
          );
        })}
        {isEditingWindow && renderAddCard()}
      </div>
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              minHeight: "20px", // ensures title area has height
            }}
          >
            Configure Metric
          </div>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        zIndex={1002}
        okText="Save"
        cancelText="Cancel"
        width="80vw"
        className="non-draggable centered-modal"
        bodyStyle={{
          maxHeight: "60vh",
          overflowY: "auto",
          padding: "24px",
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              padding: "2px 0",
            }}
          >
            <Button
              onClick={handleModalCancel}
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "6px 16px",
                background: "white",
                cursor: "pointer",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleModalOk}
              type="primary"
              style={{
                border: "none",
                borderRadius: "6px",
                padding: "6px 16px",
                color: "white",
                cursor: "pointer",
              }}
            >
              Save
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" style={{}}>
          <Card
            title="Metric Configuration"
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: "24px" }}
          >
            {/* Each Row has consistent gutter for spacing */}
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Name"
                  name="label"
                  rules={[{ required: true, message: "Please enter a name" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Object Name"
                  name="objectName"
                  rules={[{ required: true, message: "Please enter an object name" }]}
                >
                  <Select
                    placeholder="Select an object"
                    loading={fetchingObjects}
                    onChange={handleObjectChange}
                  >
                    {objects.map((object) => (
                      <Option key={object._id} value={object.name} label={object.label}>
                        {object.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item name="showGraph" valuePropName="checked" label="Show Graph">
                  <Checkbox
                    checked={form.getFieldValue("showGraph")}
                    onChange={(e) =>
                      form.setFieldsValue({ showGraph: e.target.checked })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="iconType" label="Icon Type" initialValue="user">
                  <Select>
                    <Option value="user">User</Option>
                    <Option value="users">Users</Option>
                    <Option value="cart">Cart</Option>
                    <Option value="money">Money</Option>
                    <Option value="chart">Chart</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>


            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Aggregation Type"
                  name="aggregationType"
                  rules={[{ required: true, message: "Please select aggregation type" }]}
                >
                  <Select
                    onChange={() => {
                      if (form.getFieldValue("aggregationType") !== "count") {
                        form.setFieldsValue({ fieldName: undefined });
                      }
                    }}
                  >
                    <Option value="count">Count</Option>
                    <Option value="sum">Sum</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.aggregationType !== curr.aggregationType ||
                    prev.objectName !== curr.objectName
                  }
                >
                  {() =>
                    form.getFieldValue("aggregationType") !== "count" && (
                      <Form.Item
                        label="Field Name"
                        name="fieldName"
                        rules={[{ required: true, message: "Please enter a field name" }]}
                      >
                        <Select
                          placeholder="Select a field"
                          loading={fetchingFields}
                          disabled={!form.getFieldValue("objectName")}
                          showSearch
                          optionFilterProp="children"
                        >
                          {(fieldsDataMap[editingMetricId] || []).map((field) => (
                            <Option key={field.name} value={field.name}>
                              {field.label || field.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )
                  }
                </Form.Item>
              </Col>
            </Row>

            {/* Date / DateTime Field Select */}
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Data Field"
                  name="dataField"
                  rules={[{ required: true, message: "Please select a date field" }]}
                >
                  <Select
                    placeholder="Select a date field"
                    loading={fetchingFields}
                    disabled={!form.getFieldValue("objectName")}
                    showSearch
                    optionFilterProp="children"
                  >
                    {datefields
                      ?.map((field) => (
                        <Option key={field.name} value={field.name}>
                          {field.label || field.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Filters Section */}
          {form.getFieldValue("objectName") && (
            <Card
              title="Metric Filters"
              style={{ marginBottom: 16 }}
              bodyStyle={{ padding: "24px" }}
            >
              <FiltersDynamic
                fields={fieldsDataMap[`${editingMetricId}_all`] || []}
                logic={logic}
                setLogic={setLogic}
                filters={filters}
                setFilters={setFilters}
                setValueOperator={setValueOperator}
                valueOperator={valueOperator}
                fetchLookupRecordsForExistingFilters={
                  fetchLookupRecordsForExistingFilters
                }
                fetchLookupRecords={fetchLookupRecords}
                lookupOptions={lookupOptions}
                heading="Metric Filters"
              />
            </Card>
          )}

          
        
        </Form>

      </Modal>
    </div>
  );
};
const style = (
  <style jsx>{`
    .rdg-light {
      position: relative;
      overflow: auto;
    }
    .rdg-light:hover::-webkit-scrollbar {
      width: 2.5px;
      height: 2.5px;
    }
    .rdg-light::-webkit-scrollbar {
      width: 0px;
      height: 0px;
    }
    .rdg-light::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.04);
      border-radius: 3px;
    }
    .rdg-light::-webkit-scrollbar-track {
      background: transparent;
    }


  `}</style>
);
export default MetricsWidget;